import axios from "axios";
import {
    getStoredToken,
    getStoredRefreshToken,
    setStoredSession,
    clearStoredAuth,
    restoreStoredOriginalSession,
} from "utils/auth";
import { adminLogin, refreshTokenUrl } from "./apiURLs";
import { requestReferenceOf, withRequestReference } from "./requestReference";
import {
    getRequestBearerToken,
    isDeadSessionError,
    isJwtExpiredOrExpiring,
    isTerminalAuthError,
    setRequestBearerToken,
} from "./authRefresh";

const baseURL = process.env.REACT_APP_API_URL;

export const api = axios.create({
    baseURL,
});

api.interceptors.request.use(
    async (config) => {
        let token = getStoredToken();
        const refreshToken = getStoredRefreshToken();

        // Refresh just before expiry so endpoints that still use their legacy
        // controller-level JWT checks do not have to fail first. Login stays
        // independent: a stale previous session must never block a deliberate
        // new login attempt.
        if (config.url !== adminLogin && token && refreshToken && isJwtExpiredOrExpiring(token)) {
            token = await refreshSession();
        }

        if (token) {
            setRequestBearerToken(config, token);
        }

        // Identifies this SURFACE to the API's presence tracking — the app, not
        // whoever is signed into it. The dashboard sends the same bearer token
        // as the restaurant app, so without this a vendor doing paperwork here
        // would be indistinguishable from their kitchen tablet, and the app
        // badge would go green while the tablet that prints is switched off.
        //
        // Not "admin_web" or "vendor_web": one dashboard serves both roles, and
        // /admin/impersonate hands this browser a vendor token mid-session, so a
        // role-flavoured value would flip and misreport a vendor as present.
        // See api/src/middlewares/appPresence.js.
        config.headers["X-Client"] = "dashboard_web";

        return config;
    },
    async (error) => {
        return Promise.reject(error);
    },
);

// Single-flight guard: N requests failing with 401 at once must trigger
// exactly one call to /api/auth/refresh, not one per request. Every caller
// awaits the SAME in-flight promise; the first one to resolve clears it so
// the next 401 (a genuinely new expiry) starts a fresh refresh.
let refreshPromise = null;

/**
 * The name of the browser lock that lets ONE TAB refresh at a time.
 *
 * Every open tab refreshes on its own when its access token is about to
 * expire, and the sidebar badges poll in every tab. So two tabs sent the same
 * refresh token at the same moment, the API saw a used token, and both tabs
 * were logged out. (The API now accepts that as a retry, see the API's
 * util/refreshTokens.js; this lock stops the extra rotations too.)
 */
const REFRESH_LOCK_NAME = "talabatk-dashboard-refresh";

/**
 * Runs `work` while this tab holds the refresh lock. A browser without the
 * Web Locks API runs it at once, as before.
 *
 * @template T
 * @param {() => Promise<T>} work
 * @returns {Promise<T>}
 */
const withRefreshLock = (work) =>
    typeof navigator !== "undefined" && navigator.locks?.request
        ? navigator.locks.request(REFRESH_LOCK_NAME, work)
        : work();

export function refreshSession() {
    if (!refreshPromise) {
        const presentedRefreshToken = getStoredRefreshToken();
        // The refresh token this tab really sends, read inside the lock.
        let sentRefreshToken = null;

        refreshPromise = withRefreshLock(async () => {
            // While this tab waited for the lock, another tab may have
            // refreshed: the stored refresh token is then a newer one. Use the
            // access token that tab saved, and do not rotate again.
            const storedRefreshToken = getStoredRefreshToken();
            if (storedRefreshToken !== presentedRefreshToken) {
                const sharedToken = getStoredToken();
                if (sharedToken && !isJwtExpiredOrExpiring(sharedToken)) {
                    return { sharedToken };
                }
            }

            sentRefreshToken = storedRefreshToken;
            // Use bare axios so refresh never receives an Authorization header
            // and never enters this api instance's request/response
            // interceptors.
            const response = await axios.post(
                refreshTokenUrl,
                { refreshToken: sentRefreshToken },
                { baseURL, headers: { "X-Client": "dashboard_web" } },
            );

            // A logout/login/impersonation transition may have replaced
            // credentials while the network call was in flight. Never let an
            // old response overwrite the newer session.
            if (getStoredRefreshToken() !== sentRefreshToken) {
                throw new Error("Session changed while refresh was in flight");
            }
            // Saved INSIDE the lock, so the next tab to get it reads the new
            // pair and does not rotate the used token.
            setStoredSession({
                token: response.data.token,
                refreshToken: response.data.refreshToken,
            });
            return { token: response.data.token };
        })
            .then((result) => result.sharedToken ?? result.token)
            .catch((error) => {
                if (isTerminalAuthError(error)) {
                    endLocalSession();
                }
                throw error;
            })
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
}

function endLocalSession() {
    // A terminal impersonated session must not destroy the admin's separate
    // original session. Promote it back to current storage and reload so Redux
    // hydrates the correct identity from the API.
    if (restoreStoredOriginalSession()) {
        window.location.href = "/";
        return;
    }

    clearStoredAuth();
    if (window.location.pathname !== "/login") {
        window.location.href = "/login";
    }
}

// Transparent refresh-and-retry on the API's exact 401/TOKEN_INVALID contract.
// Registered ahead of the message-
// promotion interceptor below so a successful retry never reaches it at
// all — only a request that is still failing after this runs its course
// (or couldn't attempt a refresh in the first place) falls through.
//
// A network error, 429, 5xx, or unrelated 401 must never be mistaken for a
// dead session (api#20 / auth-401-contract.md, same rule given to the mobile
// apps). Retries at most once per request
// (`_retriedAfterRefresh`), and skips entirely for the refresh call itself
// so a refresh that comes back 401 cannot recurse into refreshing again.
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const refreshToken = getStoredRefreshToken();
        const currentToken = getStoredToken();
        const requestToken = getRequestBearerToken(originalRequest);
        const isUnauthorized = error.response?.status === 401;

        // This response may have left the server before another request
        // completed a refresh. Retry once with the already-current token; do
        // not rotate the newly issued refresh token a second time.
        if (
            isUnauthorized &&
            !originalRequest?._retriedAfterRefresh &&
            requestToken &&
            currentToken &&
            requestToken !== currentToken
        ) {
            originalRequest._retriedAfterRefresh = true;
            setRequestBearerToken(originalRequest, currentToken);
            return api(originalRequest);
        }

        // A dead session is the API's 401 TOKEN_INVALID, or the profile route's
        // deliberate 404 "notfound" / 500 "unauthorized" (see authRefresh.js).
        // Without the second part a dashboard whose access token was deleted by
        // a refresh in another tab never refreshed and never logged out.
        const isDeadSession = isDeadSessionError(error);

        if (isDeadSession && !originalRequest?._retriedAfterRefresh && refreshToken) {
            originalRequest._retriedAfterRefresh = true;
            try {
                const newToken = await refreshSession();
                setRequestBearerToken(originalRequest, newToken);
                return api(originalRequest);
            } catch (refreshError) {
                // Only refreshSession's exact 401/TOKEN_INVALID branch clears
                // credentials. Network errors, 429 and 5xx remain retryable.
                return Promise.reject(refreshError);
            }
        }

        if (isDeadSession) {
            // No refresh credential, or the one retry also failed with a
            // terminal access-token response.
            endLocalSession();
        }

        return Promise.reject(error);
    },
);

// Promote backend error messages onto err.message so every hook/component
// that does `setError(err.message)` gets the real message — "vendor not
// found", "حد أدنى صورة واحدة", etc. — instead of axios's generic
// "Request failed with status code 404".
//
// We keep the original axios message available on `err.axiosMessage` in case
// anything ever wants the raw status string.
//
// A FAILURE OF THE SERVER (5xx) ALSO SHOWS A SHORT REFERENCE, the start of the
// API's request id: «internal server error (رقم المرجع: 1a2b3c4d)». Support
// finds the request in the log with it. See api/requestReference.js.
//
// The dashboard reads the API's message in two ways: `err.message` (70 places)
// and `err.response.data.message` (108 places). Both must show the reference,
// so it is added to both. The API's own text stays in `data.rawMessage`, for
// code that compares it (api/authRefresh.js).
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const data = error?.response?.data;
        const reference = requestReferenceOf(error);

        if (
            reference &&
            data &&
            typeof data === "object" &&
            typeof data.message === "string" &&
            data.rawMessage === undefined
        ) {
            data.rawMessage = data.message;
            data.message = withRequestReference(data.message, error);
        }

        const hasShownReference = typeof data?.rawMessage === "string";
        const backendMessage =
            data?.message || data?.error || (typeof data === "string" ? data : null);

        if (backendMessage) {
            error.axiosMessage = error.message;
            error.message = hasShownReference
                ? backendMessage
                : withRequestReference(backendMessage, error);
        }
        if (reference) error.requestId = reference.id;

        return Promise.reject(error);
    },
);
